import SpeedTest, { type ConfigOptions, type Results } from '@cloudflare/speedtest';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import PasswordRounded from '@mui/icons-material/PasswordRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import SpeedRounded from '@mui/icons-material/SpeedRounded';
import UploadRounded from '@mui/icons-material/UploadRounded';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Container,
  Divider,
  LinearProgress,
  Paper,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import { type ChangeEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { logInternetSpeed, logPasswordGeneration } from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import ShieldRounded from '@mui/icons-material/ShieldRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import BoltRounded from '@mui/icons-material/BoltRounded';
import TimerRounded from '@mui/icons-material/TimerRounded';
import StopRounded from '@mui/icons-material/StopRounded';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';

type PasswordMode = 'all' | 'easyToSay' | 'easyToRead';

type PasswordOptions = {
  length: number;
  mode: PasswordMode;
  includeUppercase: boolean;
  includeLowercase: boolean;
  includeNumbers: boolean;
  includeSymbols: boolean;
};

type PasswordStrength = {
  label: string;
  score: number;
  helper: string;
  color: string;
};

type SpeedPhase = 'idle' | 'latency' | 'download' | 'upload' | 'complete' | 'stopped' | 'error';

type SpeedState = {
  downloadMbps: number;
  uploadMbps: number;
  unloadedLatencyMs: number | null;
  downloadLoadedLatencyMs: number | null;
  uploadLoadedLatencyMs: number | null;
  phase: SpeedPhase;
  status: string;
  error: string | null;
  isRunning: boolean;
};

const LOWERCASE = 'abcdefghijklmnopqrstuvwxyz';
const UPPERCASE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const NUMBERS = '0123456789';
const SYMBOLS = '!@#$%^&*()_+-=[]{};:,.<>/?~';
const EASY_READ_BLOCKLIST = new Set(['0', 'O', 'o', '1', 'l', 'I']);
const BRAND_ORANGE = '#F6A938';
// Theme-dependent brand colors. They are reassigned by applyBrandPalette() at the start of each
// Home render (children render after it), so the palette follows the light/dark mode.
let BRAND_PURPLE = '#6226A7';
let BRAND_TEXT = '#26323D';
let BRAND_CREAM = '#FFF8EF';
let BRAND_PEACH = '#FFE7C8';
let BRAND_SURFACE = '#FFFFFF';
let BRAND_SURFACE_WARM = '#FFF4E6';
let BRAND_SURFACE_COOL = '#F8F2FF';
let BRAND_PAGE_MID = '#FFFDF9';

const applyBrandPalette = (dark: boolean) => {
  BRAND_PURPLE = dark ? '#B58AF0' : '#6226A7';
  BRAND_TEXT = dark ? '#EDEDF0' : '#26323D';
  BRAND_CREAM = dark ? '#171923' : '#FFF8EF';
  BRAND_PEACH = dark ? '#2A2230' : '#FFE7C8';
  BRAND_SURFACE = dark ? '#171923' : '#FFFFFF';
  BRAND_SURFACE_WARM = dark ? '#1F1B27' : '#FFF4E6';
  BRAND_SURFACE_COOL = dark ? '#1B1A2B' : '#F8F2FF';
  BRAND_PAGE_MID = dark ? '#14161D' : '#FFFDF9';
};
const HOME_SECTION_RADIUS = 2;
const HOME_CARD_RADIUS = 1.5;
const HOME_TILE_RADIUS = 1;
const DEFAULT_PASSWORD_OPTIONS: PasswordOptions = {
  length: 20,
  mode: 'all',
  includeUppercase: true,
  includeLowercase: true,
  includeNumbers: true,
  includeSymbols: true,
};
const INITIAL_SPEED_STATE: SpeedState = {
  downloadMbps: 0,
  uploadMbps: 0,
  unloadedLatencyMs: null,
  downloadLoadedLatencyMs: null,
  uploadLoadedLatencyMs: null,
  phase: 'idle',
  status: 'Ready to measure your connection',
  error: null,
  isRunning: false,
};
const SPEED_TEST_CONFIG: ConfigOptions = {
  autoStart: false,
  measureDownloadLoadedLatency: true,
  measureUploadLoadedLatency: true,
  loadedLatencyThrottle: 350,
  bandwidthFinishRequestDuration: 800,
  bandwidthMinRequestDuration: 20,
  measurements: [
    { type: 'latency', numPackets: 10 },
    { type: 'download', bytes: 1e5, count: 2, bypassMinDuration: true },
    { type: 'download', bytes: 1e6, count: 4 },
    { type: 'download', bytes: 1e7, count: 3 },
    { type: 'upload', bytes: 1e5, count: 2, bypassMinDuration: true },
    { type: 'upload', bytes: 1e6, count: 3 },
    { type: 'upload', bytes: 1e7, count: 2 },
  ],
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getRandomInt = (max: number) => {
  if (max <= 1) return 0;

  const maxUint32 = 0xffffffff;
  const bucketSize = Math.floor((maxUint32 + 1) / max);
  const limit = bucketSize * max;
  const buffer = new Uint32Array(1);

  let randomValue = 0;
  do {
    window.crypto.getRandomValues(buffer);
    randomValue = buffer[0];
  } while (randomValue >= limit);

  return randomValue % max;
};

const pickRandomChar = (pool: string) => pool[getRandomInt(pool.length)];

const shuffle = (items: string[]) => {
  const next = [...items];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = getRandomInt(index + 1);
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
  }
  return next;
};

const filterEasyReadChars = (input: string) =>
  input
    .split('')
    .filter((char) => !EASY_READ_BLOCKLIST.has(char))
    .join('');

const normalizePasswordOptions = (options: PasswordOptions): PasswordOptions => {
  const next = { ...options };

  if (next.mode === 'easyToSay') {
    next.includeNumbers = false;
    next.includeSymbols = false;
  }

  if (
    !next.includeUppercase &&
    !next.includeLowercase &&
    !next.includeNumbers &&
    !next.includeSymbols
  ) {
    next.includeLowercase = true;
  }

  if (next.mode === 'easyToSay' && !next.includeUppercase && !next.includeLowercase) {
    next.includeLowercase = true;
  }

  next.length = clamp(next.length, 4, 64);
  return next;
};

const generateEasyToSayPassword = (options: PasswordOptions) => {
  const vowels = `${options.includeLowercase ? 'aeiou' : ''}${options.includeUppercase ? 'AEIOU' : ''}`;
  const consonants = `${options.includeLowercase ? 'bcdfghjkmnpqrstvwxyz' : ''}${options.includeUppercase ? 'BCDFGHJKLMNPQRSTVWXYZ' : ''}`;

  if (!vowels || !consonants) {
    return '';
  }

  const startWithVowel = getRandomInt(2) === 0;
  const chars: string[] = [];

  for (let index = 0; index < options.length; index += 1) {
    const useVowel = startWithVowel ? index % 2 === 0 : index % 2 !== 0;
    chars.push(pickRandomChar(useVowel ? vowels : consonants));
  }

  return chars.join('');
};

const generatePassword = (options: PasswordOptions) => {
  const normalized = normalizePasswordOptions(options);

  if (normalized.mode === 'easyToSay') {
    const pronounceable = generateEasyToSayPassword(normalized);
    if (pronounceable) {
      return pronounceable;
    }
  }

  const lowercasePool =
    normalized.includeLowercase
      ? normalized.mode === 'easyToRead'
        ? filterEasyReadChars(LOWERCASE)
        : LOWERCASE
      : '';
  const uppercasePool =
    normalized.includeUppercase
      ? normalized.mode === 'easyToRead'
        ? filterEasyReadChars(UPPERCASE)
        : UPPERCASE
      : '';
  const numberPool =
    normalized.includeNumbers
      ? normalized.mode === 'easyToRead'
        ? filterEasyReadChars(NUMBERS)
        : NUMBERS
      : '';
  const symbolPool =
    normalized.includeSymbols
      ? normalized.mode === 'easyToRead'
        ? filterEasyReadChars(SYMBOLS)
        : SYMBOLS
      : '';

  const requiredPools = [lowercasePool, uppercasePool, numberPool, symbolPool].filter(Boolean);
  const combinedPool = requiredPools.join('');

  if (!combinedPool) {
    return '';
  }

  const chars = requiredPools.map((pool) => pickRandomChar(pool));
  while (chars.length < normalized.length) {
    chars.push(pickRandomChar(combinedPool));
  }

  return shuffle(chars).join('');
};

const getStrength = (options: PasswordOptions): PasswordStrength => {
  const normalized = normalizePasswordOptions(options);
  const lowercasePool =
    normalized.includeLowercase
      ? normalized.mode === 'easyToRead'
        ? filterEasyReadChars(LOWERCASE)
        : LOWERCASE
      : '';
  const uppercasePool =
    normalized.includeUppercase
      ? normalized.mode === 'easyToRead'
        ? filterEasyReadChars(UPPERCASE)
        : UPPERCASE
      : '';
  const numberPool =
    normalized.includeNumbers
      ? normalized.mode === 'easyToRead'
        ? filterEasyReadChars(NUMBERS)
        : NUMBERS
      : '';
  const symbolPool =
    normalized.includeSymbols
      ? normalized.mode === 'easyToRead'
        ? filterEasyReadChars(SYMBOLS)
        : SYMBOLS
      : '';
  const poolSize = new Set(`${lowercasePool}${uppercasePool}${numberPool}${symbolPool}`.split('')).size;
  const entropy = normalized.length * Math.log2(Math.max(poolSize, 1));
  const score = clamp(Math.round((entropy / 96) * 100), 8, 100);

  if (normalized.mode === 'easyToSay') {
    if (normalized.length < 14) {
      return {
        label: 'Fair',
        score: Math.min(score, 48),
        helper: 'Pronounceable, but length should be higher for stronger protection.',
        color: '#F59E0B',
      };
    }

    return {
      label: 'Good',
      score: Math.min(Math.max(score, 60), 76),
      helper: 'Easy to say passwords trade some complexity for memorability.',
      color: BRAND_PURPLE,
    };
  }

  if (entropy < 45) {
    return {
      label: 'Weak',
      score: Math.max(score, 20),
      helper: 'Add more length and more character groups.',
      color: '#DC2626',
    };
  }

  if (entropy < 65) {
    return {
      label: 'Fair',
      score: Math.max(score, 45),
      helper: 'Usable, but longer passwords are safer.',
      color: '#F59E0B',
    };
  }

  if (entropy < 85) {
    return {
      label: 'Good',
      score: Math.max(score, 70),
      helper: 'Balanced length and variety for most accounts.',
      color: BRAND_PURPLE,
    };
  }

  return {
    label: 'Strong',
    score: 100,
    helper: 'High-entropy password with solid resistance to guessing.',
    color: BRAND_PURPLE,
  };
};

const toMbps = (bps?: number) => (bps ? Number((bps / 1_000_000).toFixed(2)) : 0);

const formatMetric = (value: number | null, suffix: string) =>
  value === null || Number.isNaN(value) ? '--' : `${value.toFixed(value >= 100 ? 0 : 1)} ${suffix}`;

const inferSpeedState = (results: Results, phase: SpeedPhase, isRunning: boolean, error?: string | null): SpeedState => {
  const downloadLoadedLatency = results.getDownLoadedLatency();
  const uploadLoadedLatency = results.getUpLoadedLatency();

  return {
    downloadMbps: toMbps(results.getDownloadBandwidth()),
    uploadMbps: toMbps(results.getUploadBandwidth()),
    unloadedLatencyMs:
      typeof results.getUnloadedLatency() === 'number' ? results.getUnloadedLatency() ?? null : null,
    downloadLoadedLatencyMs:
      typeof downloadLoadedLatency === 'number' ? downloadLoadedLatency : null,
    uploadLoadedLatencyMs:
      typeof uploadLoadedLatency === 'number' ? uploadLoadedLatency : null,
    phase,
    status: getSpeedStatus(phase, error ?? null),
    error: error ?? null,
    isRunning,
  };
};

const getSpeedStatus = (phase: SpeedPhase, error: string | null) => {
  if (error) return error;

  switch (phase) {
    case 'latency':
      return 'Measuring unloaded latency';
    case 'download':
      return 'Measuring download speed';
    case 'upload':
      return 'Measuring upload speed';
    case 'complete':
      return 'Test complete';
    case 'stopped':
      return 'Test stopped';
    case 'error':
      return 'Unable to finish the test';
    default:
      return 'Ready to measure your connection';
  }
};

type HomeSection = 'all' | 'password' | 'speed';

export default function Home({ section = 'all' }: { section?: HomeSection }) {
  applyBrandPalette(useTheme().palette.mode === 'dark');
  const showPassword = section === 'all' || section === 'password';
  const showSpeed = section === 'all' || section === 'speed';
  const speedTestRef = useRef<SpeedTest | null>(null);

  const [passwordOptions, setPasswordOptions] = useState<PasswordOptions>(DEFAULT_PASSWORD_OPTIONS);
  const [lengthInput, setLengthInput] = useState(String(DEFAULT_PASSWORD_OPTIONS.length));
  const [password, setPassword] = useState('');
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const [speedState, setSpeedState] = useState<SpeedState>(INITIAL_SPEED_STATE);
  const [showMoreInfo, setShowMoreInfo] = useState(false);

  const strength = useMemo(() => getStrength(passwordOptions), [passwordOptions]);
  const activeCharacterGroups = useMemo(() => {
    const normalized = normalizePasswordOptions(passwordOptions);
    return [
      normalized.includeUppercase ? 'Uppercase' : null,
      normalized.includeLowercase ? 'Lowercase' : null,
      normalized.includeNumbers ? 'Numbers' : null,
      normalized.includeSymbols ? 'Symbols' : null,
    ].filter(Boolean) as string[];
  }, [passwordOptions]);

  useEffect(() => {
    setPassword(generatePassword(passwordOptions));
  }, [passwordOptions]);

  useEffect(() => {
    return () => {
      speedTestRef.current?.pause();
    };
  }, []);

  useEffect(() => {
    if (copyState !== 'copied') return undefined;

    const timeout = window.setTimeout(() => setCopyState('idle'), 1600);
    return () => window.clearTimeout(timeout);
  }, [copyState]);

  const regeneratePassword = () => {
    setPassword(generatePassword(passwordOptions));
    setCopyState('idle');
  };

  const copyPassword = async () => {
    if (!password) return;

    try {
      await navigator.clipboard.writeText(password);
      setCopyState('copied');
      void logPasswordGeneration({ password }).catch((error) => {
        console.error('Failed to save password generation log', error);
      });
    } catch (error) {
      console.error('Failed to copy password', error);
    }
  };

  const updateToggle = (key: keyof Pick<PasswordOptions, 'includeUppercase' | 'includeLowercase' | 'includeNumbers' | 'includeSymbols'>) =>
    (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setPasswordOptions((current) =>
        normalizePasswordOptions({
          ...current,
          [key]: checked,
        })
      );
    };

  const updateLength = (nextLength: number) => {
    const clamped = clamp(nextLength, 4, 64);
    setPasswordOptions((current) => ({ ...current, length: clamped }));
    setLengthInput(String(clamped));
  };

  const commitLengthInput = () => {
    const parsed = parseInt(lengthInput, 10);
    if (!Number.isNaN(parsed)) {
      updateLength(parsed);
    } else {
      setLengthInput(String(passwordOptions.length));
    }
  };

  const syncSpeedMetrics = (phase: SpeedPhase, isRunning: boolean, error?: string | null) => {
    const engine = speedTestRef.current;
    if (!engine) {
      setSpeedState((current) => ({
        ...current,
        phase,
        status: getSpeedStatus(phase, error ?? null),
        error: error ?? null,
        isRunning,
      }));
      return;
    }

    setSpeedState(inferSpeedState(engine.results, phase, isRunning, error));
  };

  const startSpeedTest = () => {
    speedTestRef.current?.pause();

    const engine = new SpeedTest(SPEED_TEST_CONFIG);
    speedTestRef.current = engine;

    setShowMoreInfo(true);
    setSpeedState({
      ...INITIAL_SPEED_STATE,
      isRunning: true,
      phase: 'latency',
      status: getSpeedStatus('latency', null),
    });

    engine.onRunningChange = (running) => {
      setSpeedState((current) => ({
        ...current,
        isRunning: running,
      }));
    };

    engine.onResultsChange = ({ type }) => {
      const phase: SpeedPhase =
        type === 'upload' ? 'upload' : type === 'download' ? 'download' : 'latency';
      syncSpeedMetrics(phase, true);
    };

    engine.onFinish = (results) => {
      setSpeedState(inferSpeedState(results, 'complete', false));
      saveInternetSpeedLog(results);
    };

    engine.onError = (error) => {
      setSpeedState((current) => ({
        ...current,
        phase: 'error',
        status: getSpeedStatus('error', error),
        error,
        isRunning: false,
      }));
    };

    engine.play();
  };

  const stopSpeedTest = () => {
    speedTestRef.current?.pause();
    speedTestRef.current = null;
    setSpeedState((current) => ({
      ...current,
      isRunning: false,
      phase: 'stopped',
      status: getSpeedStatus('stopped', null),
    }));
  };

  const connectionType =
    typeof navigator !== 'undefined' && 'connection' in navigator
      ? (navigator.connection as { effectiveType?: string; downlink?: number }).effectiveType ?? 'browser managed'
      : 'browser managed';

  const saveInternetSpeedLog = (results: Results) => {
    const metrics = inferSpeedState(results, 'complete', false);

    void logInternetSpeed({
      downloadMbps: metrics.downloadMbps,
      uploadMbps: metrics.uploadMbps,
      unloadedLatencyMs: metrics.unloadedLatencyMs,
      downloadLoadedLatencyMs: metrics.downloadLoadedLatencyMs,
      uploadLoadedLatencyMs: metrics.uploadLoadedLatencyMs,
      connectionType,
    }).catch((error) => {
      console.error('Failed to save internet speed log', error);
    });
  };

  /* ================= Dedicated, redesigned utility pages ================= */

  if (section === 'password') {
    const ACCENT = '#6226A7';
    const filledSegments = Math.max(1, Math.ceil(strength.score / 25));
    const SEGMENT_COLORS = ['#EF4444', '#F59E0B', '#84CC16', '#16A34A'];
    const poolSize =
      (passwordOptions.includeUppercase ? 26 : 0) +
      (passwordOptions.includeLowercase ? 26 : 0) +
      (passwordOptions.includeNumbers ? 10 : 0) +
      (passwordOptions.includeSymbols ? 28 : 0);
    const entropyBits = Math.round(passwordOptions.length * Math.log2(Math.max(2, poolSize)));

    const ruleTiles: {
      key: 'includeUppercase' | 'includeLowercase' | 'includeNumbers' | 'includeSymbols';
      title: string; hint: string; sample: string; on: boolean; c: [string, string];
    }[] = [
      { key: 'includeUppercase', title: 'Uppercase', hint: 'A – Z', sample: 'ABC', on: passwordOptions.includeUppercase, c: ['#6226A7', '#B06AB3'] },
      { key: 'includeLowercase', title: 'Lowercase', hint: 'a – z', sample: 'abc', on: passwordOptions.includeLowercase, c: ['#0F766E', '#2DD4BF'] },
      { key: 'includeNumbers', title: 'Numbers', hint: '0 – 9', sample: '123', on: passwordOptions.includeNumbers, c: ['#2563EB', '#60A5FA'] },
      { key: 'includeSymbols', title: 'Symbols', hint: '! @ # $ %', sample: '#@!', on: passwordOptions.includeSymbols, c: ['#E11D48', '#FB7185'] },
    ];

    const presets = [
      { label: 'PIN', o: { length: 6, includeUppercase: false, includeLowercase: false, includeNumbers: true, includeSymbols: false } },
      { label: 'Simple', o: { length: 12, includeUppercase: true, includeLowercase: true, includeNumbers: true, includeSymbols: false } },
      { label: 'Strong', o: { length: 16, includeUppercase: true, includeLowercase: true, includeNumbers: true, includeSymbols: true } },
      { label: 'Ultra', o: { length: 32, includeUppercase: true, includeLowercase: true, includeNumbers: true, includeSymbols: true } },
    ];
    const applyPreset = (o: Partial<PasswordOptions>) => {
      const next = normalizePasswordOptions({ ...passwordOptions, ...o });
      setPasswordOptions(next);
      setLengthInput(String(next.length));
    };
    const toggle = (key: typeof ruleTiles[number]['key'], on: boolean) =>
      updateToggle(key)(undefined as unknown as ChangeEvent<HTMLInputElement>, !on);

    const charColor = (ch: string) =>
      /[A-Z]/.test(ch) ? '#6226A7' : /[a-z]/.test(ch) ? '#0F172A' : /[0-9]/.test(ch) ? '#2563EB' : '#E11D48';

    return (
      <Box>
        <PageHeader title="Password Generator" subtitle="Create strong, random passwords in one click." />

        <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', lg: '1.1fr 0.9fr' }, alignItems: 'stretch' }}>
          {/* ===== Result card ===== */}
          <Paper
            elevation={0}
            className="qs-animate-in"
            sx={{
              position: 'relative', overflow: 'hidden', borderRadius: '28px', p: { xs: 2.25, md: 3.25 },
              display: 'flex', flexDirection: 'column', gap: 2.5,
              border: '1px solid rgba(98,38,167,.16)', boxShadow: '0 18px 44px rgba(98,38,167,.14)',
              background: 'linear-gradient(180deg, #ffffff 0%, #FBF8FF 100%)',
              '&::before': { content: '""', position: 'absolute', left: 0, right: 0, top: 0, height: 6, background: 'linear-gradient(90deg,#6226A7,#B06AB3,#F6A938)' },
            }}
          >
            {/* title row */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 48, height: 48, borderRadius: '16px', display: 'grid', placeItems: 'center', color: '#fff',
                    background: 'linear-gradient(135deg,#6226A7,#B06AB3)',
                    boxShadow: '0 10px 20px rgba(98,38,167,.4), inset 0 1px 0 rgba(255,255,255,.45)',
                  }}
                >
                  <PasswordRounded />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 900, fontSize: 20, lineHeight: 1.1 }}>Your password</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {passwordOptions.length} characters · ≈ {entropyBits} bits
                  </Typography>
                </Box>
              </Stack>
              <Chip
                icon={<ShieldRounded sx={{ fontSize: 18 }} />}
                label={strength.label}
                sx={{ bgcolor: `${SEGMENT_COLORS[filledSegments - 1]}22`, color: SEGMENT_COLORS[filledSegments - 1], fontWeight: 900, fontSize: 14, '& .MuiChip-icon': { color: 'inherit' } }}
              />
            </Stack>

            {/* password display */}
            <Box
              sx={{
                position: 'relative', borderRadius: '22px', p: { xs: 2.25, md: 3 }, minHeight: 120, display: 'flex', alignItems: 'center',
                bgcolor: '#fff', border: '2px solid rgba(98,38,167,.25)',
                boxShadow: 'inset 0 2px 8px rgba(98,38,167,.06), 0 10px 24px rgba(98,38,167,.10)',
              }}
            >
              <Typography
                key={password}
                component="div"
                sx={{
                  fontFamily: '"IBM Plex Mono", "SFMono-Regular", Consolas, monospace',
                  fontWeight: 800, fontSize: { xs: '1.45rem', md: '2.15rem' }, lineHeight: 1.35,
                  wordBreak: 'break-all', letterSpacing: '0.06em', width: '100%', pr: { xs: 0, sm: 5 },
                  animation: 'qsFadeInUp .35s ease backwards',
                }}
              >
                {Array.from(password).map((ch, i) => (
                  <Box key={i} component="span" sx={{ color: charColor(ch) }}>{ch}</Box>
                ))}
              </Typography>
              <Button
                onClick={copyPassword}
                aria-label="Copy password"
                sx={{
                  position: 'absolute', right: 12, top: 12, minWidth: 0, width: 40, height: 40, borderRadius: '14px',
                  color: copyState === 'copied' ? '#fff' : ACCENT,
                  bgcolor: copyState === 'copied' ? '#16A34A' : 'rgba(98,38,167,.1)',
                  '&:hover': { bgcolor: copyState === 'copied' ? '#15803D' : 'rgba(98,38,167,.2)' },
                }}
              >
                {copyState === 'copied' ? <CheckRounded /> : <ContentCopyRounded />}
              </Button>
            </Box>

            {/* strength: 4 colour steps */}
            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                <Typography sx={{ fontWeight: 800 }}>Strength</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{strength.helper}</Typography>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.75 }}>
                {SEGMENT_COLORS.map((c, i) => (
                  <Box
                    key={c}
                    sx={{
                      height: 12, borderRadius: 999,
                      bgcolor: i < filledSegments ? c : 'action.selected',
                      boxShadow: i < filledSegments ? `0 4px 10px ${c}66` : 'none',
                      transition: 'all .25s ease',
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* length */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontWeight: 800 }}>Length</Typography>
                <Box
                  sx={{
                    px: 1.75, py: 0.4, borderRadius: '12px', fontWeight: 900, fontSize: 18, color: '#fff',
                    background: 'linear-gradient(135deg,#6226A7,#B06AB3)', minWidth: 52, textAlign: 'center',
                  }}
                >
                  {passwordOptions.length}
                </Box>
              </Stack>
              <Slider
                value={passwordOptions.length}
                min={4}
                max={64}
                onChange={(_event, value) => updateLength(value as number)}
                sx={{
                  color: ACCENT, height: 10,
                  '& .MuiSlider-thumb': { width: 26, height: 26, border: '4px solid #fff', boxShadow: '0 4px 12px rgba(98,38,167,.45)' },
                  '& .MuiSlider-rail': { opacity: 0.18 },
                }}
              />
            </Box>

            {/* presets */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <AutoAwesomeRounded sx={{ color: '#F6A938' }} />
              <Typography sx={{ fontWeight: 800, mr: 0.5 }}>Presets</Typography>
              {presets.map((pr) => {
                const active =
                  passwordOptions.length === pr.o.length &&
                  passwordOptions.includeUppercase === pr.o.includeUppercase &&
                  passwordOptions.includeLowercase === pr.o.includeLowercase &&
                  passwordOptions.includeNumbers === pr.o.includeNumbers &&
                  passwordOptions.includeSymbols === pr.o.includeSymbols;
                return (
                  <Chip
                    key={pr.label}
                    label={`${pr.label} · ${pr.o.length}`}
                    clickable
                    onClick={() => applyPreset(pr.o)}
                    sx={{
                      fontWeight: 800, px: 0.5,
                      color: active ? '#fff' : ACCENT,
                      background: active ? 'linear-gradient(135deg,#6226A7,#B06AB3)' : 'rgba(98,38,167,.08)',
                      border: '1px solid', borderColor: active ? 'transparent' : 'rgba(98,38,167,.25)',
                    }}
                  />
                );
              })}
            </Stack>

            <Box sx={{ flex: 1 }} />

            {/* actions */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <Button
                size="large"
                fullWidth
                onClick={copyPassword}
                startIcon={copyState === 'copied' ? <CheckRounded /> : <ContentCopyRounded />}
                sx={{
                  py: 1.5, borderRadius: '999px', fontWeight: 900, fontSize: 16, color: '#fff',
                  background: copyState === 'copied' ? 'linear-gradient(135deg,#16A34A,#4ADE80)' : 'linear-gradient(135deg,#6226A7,#B06AB3)',
                  boxShadow: '0 14px 28px rgba(98,38,167,.4), inset 0 1px 0 rgba(255,255,255,.4)',
                  '&:hover': { transform: 'translateY(-2px)' },
                }}
              >
                {copyState === 'copied' ? 'Copied!' : 'Copy password'}
              </Button>
              <Button
                size="large"
                fullWidth
                onClick={regeneratePassword}
                startIcon={<RefreshRounded />}
                sx={{
                  py: 1.5, borderRadius: '999px', fontWeight: 900, fontSize: 16, color: ACCENT,
                  border: '2px solid rgba(98,38,167,.45)',
                  '&:hover': { bgcolor: 'rgba(98,38,167,.08)', transform: 'translateY(-2px)' },
                }}
              >
                Generate new
              </Button>
            </Stack>
          </Paper>

          {/* ===== Character tiles (same style as the speed-test result tiles) ===== */}
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gridAutoRows: '1fr' }}>
            {ruleTiles.map((r, i) => (
              <Box
                key={r.key}
                role="switch"
                aria-checked={r.on}
                tabIndex={0}
                onClick={() => toggle(r.key, r.on)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(r.key, r.on); }
                }}
                className="qs-animate-in"
                sx={{
                  position: 'relative', overflow: 'hidden', cursor: 'pointer', borderRadius: '26px', p: 2.25, minHeight: 170,
                  color: r.on ? '#fff' : 'text.secondary',
                  background: r.on ? `linear-gradient(135deg, ${r.c[0]}, ${r.c[1]})` : '#F4F2F8',
                  border: '2px solid', borderColor: r.on ? 'rgba(255,255,255,.35)' : 'rgba(0,0,0,.06)',
                  boxShadow: r.on ? `0 16px 30px ${r.c[1]}66` : 'none',
                  animationDelay: `${0.06 * (i + 1)}s`,
                  outline: 'none',
                  transition: 'transform .2s ease, box-shadow .2s ease, background .2s ease',
                  '&:hover': { transform: 'translateY(-4px)' },
                  '&:focus-visible': { boxShadow: `0 0 0 3px #fff, 0 0 0 6px ${r.c[1]}` },
                  '&::before': r.on ? {
                    content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
                    background: 'linear-gradient(135deg, rgba(255,255,255,.38), rgba(255,255,255,.06) 42%, rgba(255,255,255,0) 43%, rgba(0,0,0,.12))',
                  } : undefined,
                }}
              >
                <Typography
                  sx={{
                    position: 'absolute', right: -6, bottom: -16, fontFamily: 'monospace', fontWeight: 900, fontSize: 92,
                    lineHeight: 1, opacity: r.on ? 0.18 : 0.08, transform: 'rotate(-10deg)', pointerEvents: 'none',
                  }}
                >
                  {r.sample}
                </Typography>
                <Stack sx={{ position: 'relative', height: '100%' }} justifyContent="space-between" spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box
                      sx={{
                        minWidth: 52, height: 44, px: 1, borderRadius: '15px', display: 'grid', placeItems: 'center',
                        fontFamily: 'monospace', fontWeight: 900, fontSize: 17,
                        color: r.on ? '#fff' : r.c[0],
                        background: r.on ? 'linear-gradient(135deg, rgba(255,255,255,.45), rgba(255,255,255,.12))' : '#fff',
                        border: '1px solid', borderColor: r.on ? 'rgba(255,255,255,.5)' : 'rgba(0,0,0,.08)',
                      }}
                    >
                      {r.sample}
                    </Box>
                    <Chip
                      size="small"
                      label={r.on ? 'ON' : 'OFF'}
                      sx={{
                        fontWeight: 900, letterSpacing: 0.6,
                        bgcolor: r.on ? 'rgba(255,255,255,.9)' : 'rgba(0,0,0,.08)',
                        color: r.on ? r.c[0] : 'text.secondary',
                      }}
                    />
                  </Stack>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: 22, lineHeight: 1.1, textShadow: r.on ? '0 2px 8px rgba(0,0,0,.25)' : 'none' }}>
                      {r.title}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, opacity: 0.9 }}>{r.hint}</Typography>
                  </Box>
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  if (section === 'speed') {
    const dl = speedState.downloadMbps;
    const gaugeMax = dl <= 100 ? 100 : dl <= 500 ? 500 : 1000;
    const fraction = Math.min(1, dl / gaugeMax);
    const R = 92;
    const C = 2 * Math.PI * R;
    const arc = 0.75 * C;
    const fmt = (n: number) => n.toFixed(n >= 100 ? 0 : 1);
    const needleDeg = -135 + 270 * fraction;

    const rating =
      dl <= 0 ? null
        : dl < 5 ? { label: 'Slow', color: '#DC2626' }
        : dl < 25 ? { label: 'Okay', color: '#F59E0B' }
        : dl < 100 ? { label: 'Fast', color: '#16A34A' }
        : dl < 500 ? { label: 'Very fast', color: '#0891B2' }
        : { label: 'Blazing', color: '#6226A7' };

    const steps: { key: SpeedPhase; label: string }[] = [
      { key: 'latency', label: 'Latency' },
      { key: 'download', label: 'Download' },
      { key: 'upload', label: 'Upload' },
    ];
    const order: SpeedPhase[] = ['latency', 'download', 'upload', 'complete'];
    const currentIdx = order.indexOf(speedState.phase);

    const tiles = [
      { label: 'Download', value: `${fmt(dl)}`, unit: 'Mbps', icon: <DownloadRounded />, c: ['#11998E', '#38EF7D'] },
      { label: 'Upload', value: speedState.uploadMbps > 0 ? fmt(speedState.uploadMbps) : '--', unit: 'Mbps', icon: <UploadRounded />, c: ['#2563EB', '#60A5FA'] },
      { label: 'Latency', value: formatMetric(speedState.unloadedLatencyMs, 'ms'), unit: '', icon: <TimerRounded />, c: ['#FF9800', '#E65100'] },
      {
        label: 'Loaded latency',
        value: formatMetric(speedState.uploadLoadedLatencyMs ?? speedState.downloadLoadedLatencyMs, 'ms'),
        unit: '',
        icon: <BoltRounded />,
        c: ['#6226A7', '#B06AB3'],
      },
    ];

    const ring = keyframes`0%{transform:scale(.92);opacity:.55}100%{transform:scale(1.18);opacity:0}`;
    const idlePulse = keyframes`0%,100%{box-shadow:0 14px 28px rgba(30,58,138,.4),0 0 0 0 rgba(37,99,235,.5)}50%{box-shadow:0 14px 28px rgba(30,58,138,.4),0 0 0 14px rgba(37,99,235,0)}`;

    const ticks = Array.from({ length: 11 }, (_, i) => {
      const deg = -135 + 27 * i;
      const rad = (deg * Math.PI) / 180;
      const r1 = i % 5 === 0 ? 66 : 72;
      const r2 = 79;
      return {
        x1: 110 + r1 * Math.sin(rad), y1: 110 - r1 * Math.cos(rad),
        x2: 110 + r2 * Math.sin(rad), y2: 110 - r2 * Math.cos(rad),
        major: i % 5 === 0,
      };
    });

    return (
      <Box>
        <PageHeader
          title="Internet Speed Test"
          subtitle="Measure your download, upload and latency on the Cloudflare edge."
          actions={
            <Chip
              label={`Connection: ${connectionType}`}
              sx={{ bgcolor: 'rgba(255,255,255,.2)', color: '#fff', fontWeight: 800, border: '1px solid rgba(255,255,255,.4)' }}
            />
          }
        />

        <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', lg: '1.1fr 0.9fr' }, alignItems: 'stretch' }}>
          {/* ===== Gauge ===== */}
          <Paper
            elevation={0}
            className="qs-animate-in"
            sx={{
              position: 'relative', overflow: 'hidden', textAlign: 'center', borderRadius: '28px', p: { xs: 2.25, md: 3.25 },
              border: '1px solid rgba(37,99,235,.16)', boxShadow: '0 18px 44px rgba(30,58,138,.14)',
              background: 'linear-gradient(180deg,#ffffff 0%, #F3F7FF 100%)',
              '&::before': { content: '""', position: 'absolute', left: 0, right: 0, top: 0, height: 6, background: 'linear-gradient(90deg,#06B6D4,#2563EB,#8B5CF6)' },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Chip
                size="small"
                label={speedState.isRunning ? '● LIVE' : 'READY'}
                sx={{
                  fontWeight: 900, letterSpacing: 0.8,
                  bgcolor: speedState.isRunning ? '#DC262614' : '#2563EB14',
                  color: speedState.isRunning ? '#DC2626' : '#2563EB',
                }}
              />
              {rating && (
                <Chip size="small" label={rating.label} sx={{ fontWeight: 900, bgcolor: `${rating.color}1F`, color: rating.color }} />
              )}
            </Stack>

            <Box sx={{ position: 'relative', width: 'min(100%, 360px)', mx: 'auto', aspectRatio: '1 / 1' }}>
              {speedState.isRunning && (
                <Box
                  sx={{
                    position: 'absolute', inset: '8%', borderRadius: '50%', border: '3px solid rgba(37,99,235,.5)',
                    '@media (prefers-reduced-motion: no-preference)': { animation: `${ring} 1.6s ease-out infinite` },
                  }}
                />
              )}
              <svg viewBox="0 0 220 220" width="100%" height="100%" role="img" aria-label={`Download speed ${fmt(dl)} Mbps`}>
                <defs>
                  <linearGradient id="qsGaugeArc" x1="0" y1="1" x2="1" y2="0">
                    <stop offset="0%" stopColor="#06B6D4" />
                    <stop offset="55%" stopColor="#2563EB" />
                    <stop offset="100%" stopColor="#8B5CF6" />
                  </linearGradient>
                </defs>
                <circle
                  cx="110" cy="110" r={R} fill="none" stroke="rgba(37,99,235,.12)" strokeWidth="16" strokeLinecap="round"
                  strokeDasharray={`${arc} ${C}`} transform="rotate(135 110 110)"
                />
                <circle
                  cx="110" cy="110" r={R} fill="none" stroke="url(#qsGaugeArc)" strokeWidth="16" strokeLinecap="round"
                  strokeDasharray={`${arc * fraction} ${C}`} transform="rotate(135 110 110)"
                  style={{ transition: 'stroke-dasharray .6s ease', filter: 'drop-shadow(0 4px 8px rgba(37,99,235,.4))' }}
                />
                {ticks.map((t, i) => (
                  <line
                    key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
                    stroke={t.major ? '#1E3A8A' : 'rgba(30,58,138,.35)'} strokeWidth={t.major ? 2.4 : 1.4} strokeLinecap="round"
                  />
                ))}
                <g style={{ transform: `rotate(${needleDeg}deg)`, transformOrigin: '110px 110px', transition: 'transform .7s cubic-bezier(.3,1.35,.5,1)' }}>
                  <polygon points="110,40 105,110 115,110" fill="#1E3A8A" />
                </g>
                <circle cx="110" cy="110" r="11" fill="#1E3A8A" />
                <circle cx="110" cy="110" r="5" fill="#fff" />
                <text x="34" y="196" fontSize="10" fontWeight="700" fill="#64748B">0</text>
                <text x="170" y="196" fontSize="10" fontWeight="700" fill="#64748B">{gaugeMax}</text>
              </svg>
              <Box
                sx={{
                  position: 'absolute', left: 0, right: 0, bottom: '9%', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', pointerEvents: 'none',
                }}
              >
                <Typography sx={{ fontSize: { xs: '2.6rem', md: '3.2rem' }, fontWeight: 900, lineHeight: 1, color: '#1E3A8A', letterSpacing: '-0.04em' }}>
                  {fmt(dl)}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>Mbps download</Typography>
              </Box>
            </Box>

            {/* phase steps */}
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1.5, mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
              {steps.map((st, idx) => {
                const active = speedState.isRunning && speedState.phase === st.key;
                const done = currentIdx > idx || speedState.phase === 'complete';
                return (
                  <Chip
                    key={st.key}
                    label={st.label}
                    icon={done ? <CheckRounded /> : undefined}
                    sx={{
                      fontWeight: 800,
                      color: active || done ? '#fff' : 'text.secondary',
                      background: active
                        ? 'linear-gradient(135deg,#1E3A8A,#2563EB)'
                        : done ? 'linear-gradient(135deg,#16A34A,#4ADE80)' : 'transparent',
                      border: '1px solid', borderColor: active || done ? 'transparent' : 'divider',
                      '& .MuiChip-icon': { color: '#fff' },
                    }}
                  />
                );
              })}
            </Stack>

            <Typography sx={{ fontWeight: 700, mb: 2, color: speedState.phase === 'error' ? '#DC2626' : 'text.secondary' }}>
              {speedState.status}
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="center">
              <Button
                onClick={speedState.isRunning ? stopSpeedTest : startSpeedTest}
                startIcon={speedState.isRunning ? <StopRounded /> : speedState.phase === 'complete' ? <RefreshRounded /> : <PlayArrowRounded />}
                sx={{
                  px: 4.5, py: 1.5, borderRadius: '999px', fontWeight: 900, fontSize: 17, color: '#fff',
                  background: speedState.isRunning
                    ? 'linear-gradient(135deg,#475569,#94A3B8)'
                    : 'linear-gradient(135deg,#1E3A8A,#2563EB)',
                  boxShadow: '0 14px 28px rgba(30,58,138,.4), inset 0 1px 0 rgba(255,255,255,.4)',
                  '@media (prefers-reduced-motion: no-preference)': {
                    animation: speedState.isRunning ? 'none' : `${idlePulse} 2.4s ease-in-out infinite`,
                  },
                  '&:hover': { transform: 'translateY(-2px)' },
                }}
              >
                {speedState.isRunning ? 'Stop test' : speedState.phase === 'complete' ? 'Run again' : 'Start test'}
              </Button>
              <Button
                onClick={() => setShowMoreInfo((c) => !c)}
                sx={{ px: 3, py: 1.5, borderRadius: '999px', fontWeight: 800, border: '2px solid', borderColor: 'divider', color: 'text.primary' }}
              >
                {showMoreInfo ? 'Hide details' : 'Show details'}
              </Button>
            </Stack>
          </Paper>

          {/* ===== Result tiles ===== */}
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', alignContent: 'stretch' }}>
            {tiles.map((t, i) => (
              <Box
                key={t.label}
                className="qs-animate-in"
                sx={{
                  position: 'relative', overflow: 'hidden', color: '#fff', borderRadius: '26px', p: 2.25, minHeight: 170,
                  background: `linear-gradient(135deg, ${t.c[0]}, ${t.c[1]})`,
                  boxShadow: `0 16px 30px ${t.c[1]}55, inset 0 0 0 1px rgba(255,255,255,.35)`,
                  animationDelay: `${0.06 * (i + 1)}s`,
                  '&::before': {
                    content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
                    background: 'linear-gradient(135deg, rgba(255,255,255,.38), rgba(255,255,255,.06) 42%, rgba(255,255,255,0) 43%, rgba(0,0,0,.12))',
                  },
                }}
              >
                <Box sx={{ position: 'absolute', right: -12, bottom: -16, opacity: 0.18, transform: 'rotate(-12deg)', '& svg': { fontSize: 120 } }}>
                  {t.icon}
                </Box>
                <Stack sx={{ position: 'relative', height: '100%' }} justifyContent="space-between" spacing={1.5}>
                  <Box
                    sx={{
                      width: 44, height: 44, borderRadius: '15px', display: 'grid', placeItems: 'center',
                      background: 'linear-gradient(135deg, rgba(255,255,255,.45), rgba(255,255,255,.12))',
                      border: '1px solid rgba(255,255,255,.5)',
                    }}
                  >
                    {t.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: 30, lineHeight: 1.05, textShadow: '0 2px 8px rgba(0,0,0,.25)' }} noWrap>
                      {t.value}
                      {t.unit && <Box component="span" sx={{ fontSize: 14, fontWeight: 800, ml: 0.5, opacity: 0.9 }}>{t.unit}</Box>}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: 14, opacity: 0.95 }}>{t.label}</Typography>
                  </Box>
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>

        <Collapse in={showMoreInfo}>
          <Paper
            elevation={0}
            sx={{ mt: 2.5, borderRadius: '24px', p: { xs: 2, md: 3 }, border: '1px solid rgba(37,99,235,.14)', boxShadow: '0 14px 34px rgba(30,58,138,.08)' }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: 18, mb: 1.5 }}>Details</Typography>
            <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' } }}>
              <MetricTile label="Download speed" value={`${fmt(dl)} Mbps`} />
              <MetricTile label="Upload speed" value={speedState.uploadMbps > 0 ? `${fmt(speedState.uploadMbps)} Mbps` : '--'} />
              <MetricTile label="Unloaded latency" value={formatMetric(speedState.unloadedLatencyMs, 'ms')} />
              <MetricTile label="Loaded latency (download)" value={formatMetric(speedState.downloadLoadedLatencyMs, 'ms')} />
              <MetricTile label="Loaded latency (upload)" value={formatMetric(speedState.uploadLoadedLatencyMs, 'ms')} />
              <MetricTile label="Phase" value={speedState.phase} />
              <MetricTile label="Engine" value="Cloudflare browser speedtest" />
              <MetricTile label="Status" value={speedState.status} />
            </Box>
          </Paper>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: section === 'all' ? '100vh' : undefined,
        background:
          `radial-gradient(circle at top left, ${alpha(BRAND_PURPLE, 0.16)}, transparent 34%), radial-gradient(circle at top right, ${alpha(BRAND_ORANGE, 0.24)}, transparent 32%), linear-gradient(180deg, ${BRAND_CREAM} 0%, ${BRAND_PAGE_MID} 52%, ${BRAND_SURFACE} 100%)`,
        pb: { xs: 8, md: 12 },
      }}
    >
      <Container maxWidth={false} disableGutters sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: { xs: 3, md: 5 } }}>
        {section === 'all' && (
        <Paper
          elevation={0}
          sx={{
            mx: 'auto',
            maxWidth: 1536,
            mb: { xs: 2, md: 2.5 },
            p: { xs: 1.5, md: 2 },
            borderRadius: HOME_SECTION_RADIUS,
            border: `1px solid ${alpha(BRAND_ORANGE, 0.22)}`,
            background: `linear-gradient(135deg, ${alpha(BRAND_SURFACE_WARM, 0.98)} 0%, ${alpha(BRAND_CREAM, 0.98)} 62%, ${alpha(BRAND_PEACH, 0.92)} 100%)`,
            boxShadow: `0 14px 34px ${alpha(BRAND_ORANGE, 0.08)}`,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr', md: '1.35fr 0.85fr' },
              alignItems: 'center',
            }}
          >
            <Stack spacing={0.9}>
              <Chip
                label="Workspace + Utilities"
                sx={{
                  alignSelf: 'flex-start',
                  bgcolor: alpha(BRAND_PURPLE, 0.10),
                  color: BRAND_PURPLE,
                  fontWeight: 700,
                }}
              />
              <Typography
                sx={{
                  fontSize: { xs: '1.35rem', md: '1.9rem' },
                  fontWeight: 900,
                  letterSpacing: '-0.05em',
                  color: BRAND_TEXT,
                  lineHeight: 1.03,
                }}
              >
                Projects, chat, music, videos, passwords, and speed tests.
                <Box component="span" sx={{ color: BRAND_PURPLE }}> One cleaner workspace.</Box>
              </Typography>
              <Typography sx={{ color: alpha(BRAND_TEXT, 0.78), lineHeight: 1.45, maxWidth: 760 }}>
                QuillStack brings everyday work tools and quick utilities together, so you can manage projects, stay in chat, listen, watch, generate secure passwords, and check internet speed without jumping across apps.
              </Typography>
            </Stack>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
              }}
            >
              {[
                'Project Management',
                'Chat',
                'Music',
                'Videos',
                'Password Generator',
                'Net Speed Test',
              ].map((label) => (
                <Chip
                  key={label}
                  label={label}
                  sx={{
                    bgcolor: alpha(BRAND_SURFACE, 0.92),
                    border: `1px solid ${alpha(BRAND_ORANGE, 0.22)}`,
                    color: BRAND_TEXT,
                    fontWeight: 700,
                  }}
                />
              ))}
            </Box>
          </Box>
        </Paper>
        )}

        {showPassword && (
        <Paper
          elevation={0}
          sx={{
            mx: 'auto',
            maxWidth: 1536,
            overflow: 'hidden',
            borderRadius: HOME_SECTION_RADIUS,
            border: `1px solid ${alpha(BRAND_PURPLE, 0.18)}`,
            background:
              `linear-gradient(135deg, ${alpha(BRAND_SURFACE, 0.99)} 0%, ${alpha(BRAND_CREAM, 0.98)} 56%, ${alpha(BRAND_SURFACE_COOL, 0.98)} 100%)`,
            color: BRAND_TEXT,
            boxShadow: `0 18px 42px ${alpha(BRAND_PURPLE, 0.10)}`,
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: { xs: 2.25, md: 3 },
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr', lg: '1.02fr 0.98fr' },
              alignItems: 'start',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, md: 2 },
                borderRadius: HOME_CARD_RADIUS,
                bgcolor: alpha(BRAND_SURFACE, 0.94),
                border: `1px solid ${alpha(BRAND_PURPLE, 0.14)}`,
                backdropFilter: 'blur(12px)',
                boxShadow: `0 8px 22px ${alpha(BRAND_PURPLE, 0.05)}`,
              }}
            >
              <Stack spacing={1.5}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: '1.55rem', md: '2.2rem' },
                      lineHeight: 1.02,
                      fontWeight: 900,
                      letterSpacing: '-0.05em',
                      color: BRAND_TEXT,
                    }}
                  >
                    Generate strong passwords.
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.35 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: BRAND_TEXT }}>
                      Password length
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={lengthInput}
                      onChange={(event) => setLengthInput(event.target.value)}
                      onBlur={commitLengthInput}
                      onKeyDown={(event) => { if (event.key === 'Enter') commitLengthInput(); }}
                      slotProps={{
                        htmlInput: {
                          min: 4,
                          max: 64,
                        },
                      }}
                      sx={{ width: 110 }}
                    />
                  </Stack>
                  <Slider
                    value={passwordOptions.length}
                    min={4}
                    max={64}
                    onChange={(_event, value) => updateLength(value as number)}
                    valueLabelDisplay="auto"
                    sx={{
                      color: BRAND_PURPLE,
                      '& .MuiSlider-thumb': {
                        boxShadow: `0 0 0 8px ${alpha(BRAND_PURPLE, 0.12)}`,
                      },
                      '& .MuiSlider-rail': {
                        opacity: 0.28,
                        color: BRAND_PURPLE,
                      },
                    }}
                  />
                </Box>

                <Divider />

                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: BRAND_TEXT }}>
                    Character rules
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.75,
                      display: 'grid',
                      gap: 0.75,
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    }}
                  >
                    <OptionToggle
                      title="Uppercase letters"
                      description="Include A-Z."
                      checked={passwordOptions.includeUppercase}
                      onChange={updateToggle('includeUppercase')}
                    />
                    <OptionToggle
                      title="Lowercase letters"
                      description="Include a-z."
                      checked={passwordOptions.includeLowercase}
                      onChange={updateToggle('includeLowercase')}
                    />
                    <OptionToggle
                      title="Numbers"
                      description="Include digits 0-9."
                      checked={passwordOptions.includeNumbers}
                      onChange={updateToggle('includeNumbers')}
                    />
                    <OptionToggle
                      title="Symbols"
                      description="Include punctuation and special characters."
                      checked={passwordOptions.includeSymbols}
                      onChange={updateToggle('includeSymbols')}
                    />
                  </Box>
                </Box>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, md: 2 },
                borderRadius: HOME_CARD_RADIUS,
                bgcolor: alpha(BRAND_SURFACE, 0.96),
                border: `1px solid ${alpha(BRAND_ORANGE, 0.18)}`,
                backdropFilter: 'blur(12px)',
                boxShadow: `0 8px 22px ${alpha(BRAND_ORANGE, 0.06)}`,
              }}
            >
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: HOME_TILE_RADIUS,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: alpha(BRAND_ORANGE, 0.18),
                      color: BRAND_PURPLE,
                    }}
                  >
                    <PasswordRounded />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: BRAND_TEXT, lineHeight: 1.1 }}>
                      Password preview
                    </Typography>
                  </Box>
                </Stack>

                <Paper
                  elevation={0}
                  sx={{
                    p: 1.4,
                    borderRadius: HOME_TILE_RADIUS,
                    bgcolor: alpha(BRAND_SURFACE_COOL, 0.98),
                    border: `1px solid ${alpha(BRAND_PURPLE, 0.18)}`,
                    boxShadow: `0 10px 22px ${alpha(BRAND_PURPLE, 0.08)}`,
                  }}
                >
                  <Typography sx={{ color: alpha(BRAND_TEXT, 0.68), fontWeight: 700 }}>
                    Generated password
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.6,
                      color: BRAND_PURPLE,
                      fontWeight: 800,
                      fontSize: { xs: '0.95rem', md: '1.05rem' },
                      lineHeight: 1.25,
                      wordBreak: 'break-all',
                      letterSpacing: '0.02em',
                      fontFamily: '"IBM Plex Mono", "SFMono-Regular", monospace',
                    }}
                  >
                    {password}
                  </Typography>
                </Paper>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button size="medium" variant="contained" startIcon={<ContentCopyRounded />} onClick={copyPassword}>
                    {copyState === 'copied' ? 'Copied' : 'Copy Password'}
                  </Button>
                  <Button size="medium" variant="outlined" startIcon={<RefreshRounded />} onClick={regeneratePassword}>
                    Regenerate Password
                  </Button>
                </Stack>

                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: HOME_CARD_RADIUS,
                    border: `1px solid ${alpha(BRAND_ORANGE, 0.16)}`,
                    bgcolor: alpha(BRAND_SURFACE_WARM, 0.82),
                  }}
                >
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography sx={{ fontWeight: 800, color: BRAND_TEXT }}>
                        Strength meter
                      </Typography>
                      <Typography sx={{ fontWeight: 800, color: strength.color }}>
                        {strength.label}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={strength.score}
                      sx={{
                        height: 8,
                        borderRadius: 1,
                        bgcolor: alpha(strength.color, 0.16),
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 1,
                          backgroundColor: strength.color,
                        },
                      }}
                    />
                  </Stack>
                </Paper>

                <Box
                  sx={{
                    display: 'grid',
                    gap: 0.9,
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                  }}
                >
                  <MetricTile label="Strength" value={strength.label} />
                  <MetricTile label="Length" value={`${passwordOptions.length} characters`} />
                  <MetricTile label="Groups" value={activeCharacterGroups.join(', ') || 'Lowercase'} />
                </Box>
              </Stack>
            </Paper>
          </Box>
        </Paper>
        )}
      </Container>

      {showSpeed && (
      <Container maxWidth={false} disableGutters sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: { xs: 2.5, md: 3.5 } }}>
        <Box sx={{ maxWidth: 1536, mx: 'auto', display: 'grid', gap: 3 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: HOME_SECTION_RADIUS,
              border: `1px solid ${alpha(BRAND_ORANGE, 0.18)}`,
              background: `linear-gradient(180deg, ${alpha(BRAND_SURFACE_COOL, 0.98)} 0%, ${alpha(BRAND_SURFACE, 0.98)} 100%)`,
              overflow: 'hidden',
              boxShadow: `0 18px 40px ${alpha(BRAND_ORANGE, 0.06)}`,
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: { xs: 1.5, md: 2 },
                borderBottom: `1px solid ${alpha(BRAND_PURPLE, 0.12)}`,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 900, color: BRAND_TEXT }}>
                  Net Speed Test
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip icon={<SpeedRounded />} label="Cloudflare edge" sx={{ bgcolor: alpha(BRAND_ORANGE, 0.18), color: BRAND_PURPLE, fontWeight: 700 }} />
                <Chip label={`Connection: ${connectionType}`} sx={{ bgcolor: alpha(BRAND_PURPLE, 0.10), color: BRAND_TEXT, fontWeight: 700 }} />
              </Stack>
            </Box>

            <Box
              sx={{
                p: { xs: 2, md: 2.5 },
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: { xs: '1fr', lg: '1.15fr 0.85fr' },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.75, md: 2.25 },
                  borderRadius: HOME_CARD_RADIUS,
                  bgcolor: alpha(BRAND_SURFACE, 0.98),
                  border: `1px solid ${alpha(BRAND_PURPLE, 0.12)}`,
                  boxShadow: `0 8px 20px ${alpha(BRAND_PURPLE, 0.05)}`,
                }}
              >
                <Stack alignItems="center" spacing={1.25} sx={{ textAlign: 'center' }}>
                  <Typography sx={{ color: alpha(BRAND_TEXT, 0.68), fontWeight: 700 }}>
                    Your internet speed is
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      py: { xs: 1, md: 1.4 },
                      borderRadius: HOME_CARD_RADIUS,
                      background:
                        'radial-gradient(circle at center, rgba(98,38,167,0.12) 0%, rgba(98,38,167,0.03) 48%, transparent 70%)',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '3.2rem', md: '4.8rem' },
                        lineHeight: 0.95,
                        fontWeight: 900,
                        color: BRAND_PURPLE,
                        letterSpacing: '-0.05em',
                      }}
                    >
                      {speedState.downloadMbps.toFixed(speedState.downloadMbps >= 100 ? 0 : 1)}
                    </Typography>
                    <Typography sx={{ mt: 1, fontSize: { xs: '1rem', md: '1.15rem' }, color: alpha(BRAND_TEXT, 0.76) }}>
                      Mbps download
                    </Typography>
                  </Box>

                  <Chip
                    label={speedState.status}
                    sx={{
                      fontWeight: 700,
                      bgcolor:
                        speedState.phase === 'error'
                          ? alpha('#DC2626', 0.12)
                          : alpha(BRAND_PURPLE, 0.12),
                      color:
                        speedState.phase === 'error'
                          ? '#DC2626'
                          : BRAND_PURPLE,
                    }}
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} sx={{ width: '100%', justifyContent: 'center' }}>
                    {speedState.isRunning ? (
                      <Button size="medium" variant="contained" onClick={stopSpeedTest} startIcon={<SpeedRounded />}>
                        Stop Test
                      </Button>
                    ) : (
                      <Button size="medium" variant="contained" onClick={startSpeedTest} startIcon={<SpeedRounded />}>
                        {speedState.phase === 'complete' ? 'Run Again' : 'Start Test'}
                      </Button>
                    )}
                    <Button
                      size="medium"
                      variant="outlined"
                      onClick={() => setShowMoreInfo((current) => !current)}
                    >
                      {showMoreInfo ? 'Hide more info' : 'Show more info'}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, md: 2 },
                  borderRadius: HOME_CARD_RADIUS,
                  border: `1px solid ${alpha(BRAND_ORANGE, 0.18)}`,
                  background:
                    `linear-gradient(160deg, ${alpha(BRAND_SURFACE_WARM, 0.98)} 0%, ${alpha(BRAND_PEACH, 0.92)} 100%)`,
                  color: BRAND_TEXT,
                  boxShadow: `0 8px 20px ${alpha(BRAND_ORANGE, 0.06)}`,
                }}
              >
                <Typography sx={{ color: alpha(BRAND_TEXT, 0.72), fontWeight: 700 }}>
                  Quick summary
                </Typography>
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  <SpeedSummaryRow icon={<DownloadRounded />} label="Download" value={`${speedState.downloadMbps.toFixed(speedState.downloadMbps >= 100 ? 0 : 1)} Mbps`} />
                  <SpeedSummaryRow icon={<UploadRounded />} label="Upload" value={speedState.uploadMbps > 0 ? `${speedState.uploadMbps.toFixed(speedState.uploadMbps >= 100 ? 0 : 1)} Mbps` : '--'} />
                  <SpeedSummaryRow icon={<SpeedRounded />} label="Unloaded latency" value={formatMetric(speedState.unloadedLatencyMs, 'ms')} />
                  <SpeedSummaryRow
                    icon={<SpeedRounded />}
                    label="Loaded latency"
                    value={formatMetric(
                      speedState.uploadLoadedLatencyMs ?? speedState.downloadLoadedLatencyMs,
                      'ms'
                    )}
                  />
                </Stack>
              </Paper>
            </Box>

            <Collapse in={showMoreInfo}>
              <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 } }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.75, md: 2.25 },
                    borderRadius: HOME_CARD_RADIUS,
                    border: `1px solid ${alpha(BRAND_PURPLE, 0.12)}`,
                    bgcolor: alpha(BRAND_SURFACE, 0.98),
                    boxShadow: `0 8px 20px ${alpha(BRAND_PURPLE, 0.05)}`,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 800, color: BRAND_TEXT, mb: 1.25 }}>
                    More info
                  </Typography>

                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1,
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                        lg: 'repeat(4, minmax(0, 1fr))',
                      },
                    }}
                  >
                    <MetricTile label="Download speed" value={`${speedState.downloadMbps.toFixed(speedState.downloadMbps >= 100 ? 0 : 1)} Mbps`} />
                    <MetricTile label="Upload speed" value={speedState.uploadMbps > 0 ? `${speedState.uploadMbps.toFixed(speedState.uploadMbps >= 100 ? 0 : 1)} Mbps` : '--'} />
                    <MetricTile label="Unloaded latency" value={formatMetric(speedState.unloadedLatencyMs, 'ms')} />
                    <MetricTile label="Loaded latency (download)" value={formatMetric(speedState.downloadLoadedLatencyMs, 'ms')} />
                    <MetricTile label="Loaded latency (upload)" value={formatMetric(speedState.uploadLoadedLatencyMs, 'ms')} />
                    <MetricTile label="Phase" value={speedState.phase} />
                    <MetricTile label="Measurement engine" value="Cloudflare browser speedtest" />
                    <MetricTile label="Status" value={speedState.status} />
                  </Box>
                </Paper>
              </Box>
            </Collapse>
          </Paper>
        </Box>
      </Container>
      )}
    </Box>
  );
}

function OptionToggle({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.1,
        borderRadius: HOME_TILE_RADIUS,
        border: `1px solid ${alpha(BRAND_PURPLE, 0.10)}`,
        bgcolor: disabled ? alpha(BRAND_PEACH, 0.55) : alpha(BRAND_SURFACE, 0.92),
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={1.5} alignItems="center">
        <Box>
          <Typography sx={{ fontWeight: 800, color: BRAND_TEXT, lineHeight: 1.15 }}>{title}</Typography>
          <Typography sx={{ mt: 0.2, color: alpha(BRAND_TEXT, 0.72), fontSize: 13 }}>{description}</Typography>
          {disabled ? (
            <Typography sx={{ mt: 0.45, color: BRAND_PURPLE, fontSize: 12, fontWeight: 700 }}>
              Disabled in easy-to-say mode.
            </Typography>
          ) : null}
        </Box>
        <Switch checked={checked} disabled={disabled} onChange={onChange} />
      </Stack>
    </Paper>
  );
}

function MetricTile({ label, value }: { label: string; value: string }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.35,
        borderRadius: HOME_TILE_RADIUS,
        border: `1px solid ${alpha(BRAND_PURPLE, 0.10)}`,
        bgcolor: alpha(BRAND_SURFACE, 0.94),
      }}
    >
      <Typography sx={{ fontSize: 13, color: alpha(BRAND_TEXT, 0.68), fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ mt: 0.45, color: BRAND_TEXT, fontWeight: 800, wordBreak: 'break-word', lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Paper>
  );
}

function SpeedSummaryRow({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        p: 1.5,
        borderRadius: HOME_TILE_RADIUS,
        bgcolor: alpha(BRAND_SURFACE, 0.9),
        border: `1px solid ${alpha(BRAND_PURPLE, 0.10)}`,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: HOME_TILE_RADIUS,
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(BRAND_ORANGE, 0.18),
            color: BRAND_PURPLE,
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ color: alpha(BRAND_TEXT, 0.76), fontWeight: 700 }}>{label}</Typography>
      </Stack>
      <Typography sx={{ fontWeight: 800, color: BRAND_TEXT }}>{value}</Typography>
    </Stack>
  );
}
